import React from 'react';

import FaIconUvilo, { type FaIconUviloProps } from './FaIconUvilo';
import CircularProgress from '@mui/material/CircularProgress';
import Fab, { type FabProps, type FabPropsSizeOverrides } from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import { type TooltipProps } from '@mui/material/Tooltip';
import type { OverridableStringUnion } from '@mui/types';
import clsx from 'clsx';

type IconFabProps = Omit<FabProps, 'size'> &
  Pick<FaIconUviloProps, 'prefix' | 'faProps'> & {
    icon: string;
    loading?: boolean;
    size?: OverridableStringUnion<'small' | 'medium' | 'large', FabPropsSizeOverrides>;
    title?: string;
    tooltipPlacement?: TooltipProps['placement'];
  };

export default function IconFab({
  icon,
  loading,
  disabled,
  size,
  prefix,
  faProps,
  title,
  tooltipPlacement,
  children,
  ...fabProps
}: IconFabProps) {
  'use memo';

  const iconSize = size === 'small' ? '1x' : size === 'medium' ? 'lg' : 'xl';
  const progressSize = size === 'small' ? 16 : size === 'medium' ? 20 : 24;

  const fab = (
    <Fab
      disabled={loading || disabled}
      color="primary"
      size={size || 'large'}
      {...fabProps}
      className={clsx('icon-fab', fabProps.className)}
    >
      {loading ? (
        <CircularProgress size={progressSize} thickness={6} />
      ) : (
        <FaIconUvilo icon={icon} prefix={prefix} size={iconSize} faProps={faProps} />
      )}
      {children}
    </Fab>
  );

  return title && !loading ? (
    <Tooltip title={title} placement={tooltipPlacement} className="icon-fab">
      {fab}
    </Tooltip>
  ) : (
    fab
  );
}
